export const kioskDwellSeconds = 360;
export const kioskPlaythroughSeconds = 330;
export type KioskStageMode = "round" | "episode" | "rivalry" | "upset" | "live";
export const kioskModeLabel: Record<KioskStageMode, string> = {
  round: "Round mode",
  episode: "Episode mode",
  rivalry: "Rivalry mode",
  upset: "Upset reel",
  live: "Live only",
};
export function kioskNextMode(mode: KioskStageMode): KioskStageMode {
  switch (mode) {
    case "round":
      return "episode";
    case "episode":
      return "rivalry";
    case "rivalry":
      return "upset";
    case "upset":
      return "live";
    case "live":
      return "round";
  }
}
export function kioskStageMode(param: string | null): KioskStageMode | null {
  if (param == null) return null;

  switch (param) {
    case "round":
    case "episode":
    case "rivalry":
    case "upset":
    case "live":
      return param;
    default:
      return "round";
  }
}
export const kioskEmptyDwellSeconds = 15;
const kioskTitleCardSeconds = 5;
export const kioskStandingsCardSeconds = 8;
const kioskMinTheaterSeconds = 30;
export type KioskSegment = "title" | "theater" | "standings";
export function kioskSegment({
  dwell,
  limit,
  openedByRotation = true,
}: {
  readonly dwell: number;
  readonly limit: number;
  readonly openedByRotation?: boolean;
}): KioskSegment {
  const fitsTheater =
    limit >=
    kioskTitleCardSeconds + kioskStandingsCardSeconds + kioskMinTheaterSeconds;

  if (!fitsTheater) return "title";

  if (dwell < kioskTitleCardSeconds) {
    return openedByRotation ? "title" : "theater";
  }

  if (dwell >= limit - kioskStandingsCardSeconds) return "standings";

  return "theater";
}
type KioskLeagueSnapshot = {
  readonly pool: {
    readonly live: unknown;
    readonly replays: unknown[];
  };
  readonly playlist: unknown[];
};
function rotationTier(state: KioskLeagueSnapshot | undefined): number {
  if (!state) return 2;

  if (state.pool.live) return 0;

  if (state.playlist.length > 0 || state.pool.replays.length > 0) return 1;

  return 2;
}
export function kioskRotationOrder<
  L extends {
    readonly id: string;
  },
>({
  leagues,
  states,
}: {
  readonly leagues: L[];
  readonly states: Record<string, KioskLeagueSnapshot | undefined>;
}): L[] {
  return leagues
    .map((league, index) => ({
      league,
      index,
      tier: rotationTier(states[league.id]),
    }))
    .toSorted((a, b) => a.tier - b.tier || a.index - b.index)
    .map((entry) => entry.league);
}
export function kioskNextRound<
  R extends {
    readonly roundNumber: number;
  },
>({
  playlist,
  currentRound,
  seenRounds,
}: {
  readonly playlist: readonly R[];
  readonly currentRound: number | null;
  readonly seenRounds: ReadonlySet<number>;
}): R | null {
  if (playlist.length === 0) return null;

  const fresh = playlist.find(
    (entry) =>
      entry.roundNumber !== currentRound && !seenRounds.has(entry.roundNumber),
  );

  if (fresh) return fresh;

  return (
    playlist.find((entry) => entry.roundNumber !== currentRound) ??
    playlist[0] ??
    null
  );
}
